/**
 * Input/output contracts for the HubSpot CRM read callables
 * (ADR-169 Stage 2, revised to the review-first model — ADR-172).
 *
 * Both callables are READS: they surface CRM contact data to the admin
 * portal and write nothing anywhere. Linking is organizer-driven and
 * client-committed — the admin's match dialog shows candidates, the
 * organizer confirms, the pulled values land in the person's edit form
 * for review, and everything (profile fields, the "From HubSpot" notes
 * block, and the `personInternals.hubspotContactId`/`hubspotRepcrd`
 * pointer) persists only when the organizer saves, through the normal
 * form-save paths. There is no server-side link write and no background
 * sync; the app never writes back to HubSpot (read-only scopes).
 *
 * Authorization (both callables): global organizer or admin.
 */

import { z } from 'zod';

/**
 * One CRM contact, carrying the Stage 2 pull set: identity fields for
 * display, the profile-field values (jobTitle/company/linkedIn/phone/
 * city/state), and the LinkedIn bio for the "From HubSpot" notes
 * block. FinTrx segmentation attributes (credential, rep registration
 * type, vendor dataset) are deliberately NOT pulled — they serve CRM
 * list-slicing, not event organizers (owner call, ADR-172). Everything
 * but `contactId` may be null.
 */
export interface HubSpotContactSuggestion {
  contactId: string;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  jobTitle: string | null;
  /** Name of the primary associated company. */
  company: string | null;
  linkedIn: string | null;
  phone: string | null;
  city: string | null;
  state: string | null;
  repcrd: string | null;
  /** `linkedinbio` — the one usable profile-blurb property (ADR-172). */
  linkedInBio: string | null;
}

// ── searchHubSpotContacts ──

/**
 * Contact search. Full-email queries resolve by exact match (HubSpot's
 * unique contact key, outside the search quota); anything else goes
 * through the CRM search API (name / email / company / phone). The
 * match dialog fires this twice on open — once with the person's email,
 * once with their name — and merges.
 */
export const searchHubSpotContactsInputSchema = z
  .object({
    query: z.string().min(3).max(200),
  })
  .strict();

export type SearchHubSpotContactsInput = z.infer<
  typeof searchHubSpotContactsInputSchema
>;

export interface SearchHubSpotContactsResponse {
  success: true;
  results: HubSpotContactSuggestion[];
}

// ── getHubSpotContact ──

/**
 * Single-contact fetch by record ID — the match dialog's paste-URL path
 * and the list-page → detail-page handoff (`?hubspotContact={id}`).
 * Unknown IDs are `not-found`.
 */
export const getHubSpotContactInputSchema = z
  .object({
    contactId: z.string().min(1).max(64),
  })
  .strict();

export type GetHubSpotContactInput = z.infer<
  typeof getHubSpotContactInputSchema
>;

export interface GetHubSpotContactResponse {
  success: true;
  contact: HubSpotContactSuggestion;
}
